import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { PropsOf } from '@headlessui/react/dist/types';
import { twMerge } from 'tailwind-merge';
import { TbPlus, TbTrash, TbChevronLeft } from 'react-icons/tb';
import { Link } from 'react-router-dom';
import {
  BankTransaction,
  BankTransactionWithParticipation,
  BankTransactionParticipation,
} from '../../../types/bankAccount';
import { RootState } from '../../../redux/reducers';
import { ConnectionType } from '../../../redux/slices/connectionsSlice';
import Avatar from '../../components/misc/Avatar';
import PageHeader from '../../components/misc/PageHeader';
import SearchBar from '../../components/forms/SearchBar';
import EmptyScreenView from '../../components/misc/EmptyScreenView';
import {
  BaseTable,
  BaseTableBody,
  BaseTableDataRow,
  BaseTableHeader,
  BaseTableHeaderRow,
  BaseTableSpacerRow,
} from '../../components/tables/BaseTable';
import { formatDate } from '../../../utils/stringUtils';
import * as bankAccountService from '../../../services/bankAccountService';
import DeclarationModal from '../../components/modals/DeclarationModal';
import useSelectedBusiness from '../../../hooks/business/useSelectedBusiness';
import { formatEuroCents } from '../../../utils/bankAccountUtil';
import Button from '../../components/buttons/Button';
import { ActionMenu, ActionMenuButton } from '../../components/menus/ActionMenu';
import connectionService from '../../../services/connectionService';

interface DeclarationsPageProps {
  hideHeader?: boolean;
}

export default function DeclarationsPage({
  hideHeader = false,
}: DeclarationsPageProps): JSX.Element | null {
  const { t, i18n } = useTranslation('', { keyPrefix: 'page.financial.declaration' });
  const { t: tEmpty } = useTranslation('', { keyPrefix: 'page.financial.declaration.emptyState' });
  const [declarations, setDeclarations] = useState<BankTransactionWithParticipation[] | null>();
  const connections = useSelector((state: RootState) => state.connections);
  const business = useSelectedBusiness();
  const [search, setSearch] = useState<string>('');
  const [splitModalDeclaration, setSplitModalDeclaration] = useState<BankTransaction | null>(null);
  const [splitModalOpen, setSplitModalOpen] = useState<boolean>(false);
  const [createDeclarationModalOpen, setCreateDeclarationModalOpen] = useState<boolean>(false);

  useEffect(() => {
    // Filter transactions to only show declarations (transactions with payerId)
    bankAccountService.getAllTransactions().then((transactions) => {
      const declarationTransactions = transactions.filter((transaction) => transaction.payerId);
      setDeclarations(declarationTransactions);
    });
    connectionService.getConnections();
  }, [business]);

  const openDeclarationModal = (declaration: BankTransactionWithParticipation) => {
    setSplitModalDeclaration(declaration);
    setTimeout(() => setSplitModalOpen(true), 10);
  };

  if (!business) return null;

  return (
    <>
      {!hideHeader ? (
        <>
          <Link to="/financial/administration">
            <Button className="mb-2 gap-1 p-0">
              <TbChevronLeft size={20} /> Back
            </Button>
          </Link>
          <PageHeader
            title={t('title')}
            subtitle={t('subtitle')}
            primaryButton={
              declarations && declarations.length > 0 ? (
                <Button variant="primary-dark" onClick={() => setCreateDeclarationModalOpen(true)}>
                  <TbPlus /> {t('addDeclaration')}
                </Button>
              ) : undefined
            }
          />
        </>
      ) : (
        <div className="mb-4 flex items-center justify-between">
          {declarations && declarations.length > 0 && (
            <>
              <SearchBar className="max-w-[380px]" search={search} setSearch={setSearch} />
              <Button variant="primary-dark" onClick={() => setCreateDeclarationModalOpen(true)}>
                <TbPlus /> {t('addDeclaration')}
              </Button>
            </>
          )}
        </div>
      )}

      <div className="flex flex-1 flex-col gap-5 pb-4">
        {!declarations ? (
          <EmptyScreenView subtitle="" />
        ) : declarations.length === 0 ? (
          <EmptyScreenView
            title={tEmpty('title')}
            subtitle={tEmpty('subtitle')}
            primaryButton={
              <Button variant="primary-dark" onClick={() => setCreateDeclarationModalOpen(true)}>
                <TbPlus /> {tEmpty('createFirstButton')}
              </Button>
            }
          />
        ) : (
          <>
            <div className="flex flex-col gap-8">
              <div className="relative w-full overflow-x-auto">
                <BaseTable>
                  <BaseTableHeader>
                    <BaseTableHeaderRow>
                      <HeaderCell>{t('table.date')}</HeaderCell>
                      <HeaderCell>{t('table.description')}</HeaderCell>
                      <HeaderCell>{t('table.amount')}</HeaderCell>
                      <HeaderCell>{t('table.paidBy')}</HeaderCell>
                      <HeaderCell>{t('table.paidFor')}</HeaderCell>
                      <HeaderCell>{t('table.actions')}</HeaderCell>
                    </BaseTableHeaderRow>
                  </BaseTableHeader>
                  <BaseTableBody>
                    <BaseTableSpacerRow />
                    {declarations
                      .filter((declaration) => {
                        if (search.trim()) {
                          const searchLower = search.toLowerCase();
                          return (
                            declaration.description?.toLowerCase().includes(searchLower) ||
                            declaration.participations?.some((participation) => {
                              const connection = connections.find(
                                (c) => +c.id === +participation.connectionId,
                              );
                              if (connection) {
                                const fullName =
                                  `${connection.fields.FIRST_NAME} ${connection.fields.LAST_NAME}`.toLowerCase();
                                return fullName.includes(searchLower);
                              }
                              return false;
                            })
                          );
                        }
                        return true;
                      })
                      .map((declaration, i) => (
                        <BaseTableDataRow
                          className="group cursor-pointer transition-all hover:bg-secondary-200"
                          key={`${declaration.transactionId}_declaration_${i}`}>
                          <DataCell onClick={() => openDeclarationModal(declaration)}>
                            {formatDate(new Date(declaration.date))}
                          </DataCell>
                          <DataCell onClick={() => openDeclarationModal(declaration)}>
                            <p className="text-sm">
                              {declaration.description.slice(0, 50)}
                              {declaration.description.length >= 50 && '...'}
                            </p>
                          </DataCell>
                          <DataCell onClick={() => openDeclarationModal(declaration)}>
                            {formatEuroCents(declaration.amount, i18n.language)}
                          </DataCell>
                          <DataCell onClick={() => openDeclarationModal(declaration)}>
                            <PaidByInfo declaration={declaration} />
                          </DataCell>
                          <DataCell onClick={() => openDeclarationModal(declaration)}>
                            <PaidForInfo declaration={declaration} />
                          </DataCell>
                          <DataCell>
                            <ActionMenu>
                              <ActionMenuButton
                                icon={TbTrash}
                                text={t('table.delete')}
                                className="text-danger"
                                onClick={() => {
                                  bankAccountService
                                    .deleteTransaction(declaration.transactionId)
                                    .then(() => {
                                      setDeclarations((prev) =>
                                        prev
                                          ? prev.filter(
                                              (decl) =>
                                                decl.transactionId !== declaration.transactionId,
                                            )
                                          : prev,
                                      );
                                      toast.success(t('success.deleted'));
                                    })
                                    .catch((error) => {
                                      toast.error(
                                        t('error.deletionFailed', { error: error.message }),
                                      );
                                    });
                                }}
                              />
                            </ActionMenu>
                          </DataCell>
                        </BaseTableDataRow>
                      ))}
                  </BaseTableBody>
                </BaseTable>
              </div>
            </div>
          </>
        )}

        {splitModalDeclaration && (
          <DeclarationModal
            open={splitModalOpen}
            setOpen={(open) => {
              if (open) {
                setSplitModalDeclaration(splitModalDeclaration);
                setTimeout(() => setSplitModalOpen(true), 10);
              } else {
                setSplitModalOpen(false);
                setTimeout(() => setSplitModalDeclaration(null), 200);
              }
            }}
            initialTransaction={splitModalDeclaration}
            onSplitSuccess={(declaration) => {
              if (!splitModalDeclaration) return;

              bankAccountService
                .updateBankTransaction(splitModalDeclaration?.transactionId, declaration)
                .then((updatedDeclaration) => {
                  setDeclarations((prev) =>
                    prev
                      ? prev.map((decl) =>
                          decl.transactionId === splitModalDeclaration.transactionId
                            ? updatedDeclaration
                            : decl,
                        )
                      : prev,
                  );
                  toast.success(t('success.updated'));
                  setSplitModalOpen(false);
                  setTimeout(() => setSplitModalDeclaration(null), 200);
                });
            }}
          />
        )}

        <DeclarationModal
          open={createDeclarationModalOpen}
          setOpen={setCreateDeclarationModalOpen}
          onSplitSuccess={(declaration) => {
            bankAccountService
              .createTransactionWithParticipations(declaration)
              .then((newDeclaration) => {
                toast.success(t('success.created'));
                setCreateDeclarationModalOpen(false);
                setDeclarations((prev) => (prev ? [newDeclaration, ...prev] : [newDeclaration]));
              })
              .catch(() => {
                toast.error(t('error.declarationCreation'));
              });
          }}
        />
      </div>
    </>
  );
}

function AvatarList({ participants }: { participants: BankTransactionParticipation[] }) {
  const connectionsAll = useSelector((state: RootState) => state.connections);
  const connections = connectionsAll.filter((c) => c.type === ConnectionType.B2C_CONNECTION);

  const displayParticipants = participants
    .map((p) => connections.find((connection) => +connection.id === +p.connectionId))
    .filter(Boolean);

  return (
    <div className="flex items-center gap-[-8px]">
      {displayParticipants.length === 1 ? (
        <div className="flex items-center gap-2">
          <Avatar
            className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-900 text-xs font-bold text-secondary-50 shadow-md"
            src={displayParticipants[0]?.picture}
            alias={`${displayParticipants[0]?.fields.FIRST_NAME}+${displayParticipants[0]?.fields.LAST_NAME}`}
          />
          {displayParticipants[0]?.fields.FIRST_NAME} {displayParticipants[0]?.fields.LAST_NAME}
        </div>
      ) : (
        displayParticipants
          .slice(0, 2)
          .map((c, index) => (
            <Avatar
              key={`avatar_declarations_${index}_${c?.id}`}
              className={`relative z-[${10 - index}] flex h-8 w-8 items-center justify-center rounded-full ${index === 0 ? 'bg-secondary-200' : index === 1 ? 'bg-primary-300' : 'bg-primary-900'} text-xs font-bold text-white shadow-md ${index === 0 ? '' : '-ml-2'}`}
              src={c?.picture}
              alias={`${c?.fields.FIRST_NAME}+${c?.fields.LAST_NAME}`}
            />
          ))
      )}
      {participants.length > 2 && (
        <div
          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-blue-900 text-sm font-bold text-white shadow-md"
          style={{
            marginLeft: '-8px',
            zIndex: 1,
          }}>
          +{participants.length - 2}
        </div>
      )}
    </div>
  );
}

function PaidByInfo({
  declaration,
}: {
  declaration: BankTransactionWithParticipation;
}): JSX.Element | null {
  const connections = useSelector((state: RootState) => state.connections);
  const business = useSelectedBusiness();

  if (!business) return null;

  // For declarations, the payer is the person who made the declaration
  const payer = connections.find((x) => +x.id === declaration.payerId);
  if (!payer) return null;

  const alias = `${payer.fields.FIRST_NAME}+${payer.fields.LAST_NAME}`;

  return (
    <div className="flex items-center gap-2">
      <Avatar className="h-8 w-8 rounded-full" src={payer?.picture} alias={alias} />
      <span>
        {payer.fields.FIRST_NAME} {payer.fields.LAST_NAME}
      </span>
    </div>
  );
}

function PaidForInfo({
  declaration,
}: {
  declaration: BankTransactionWithParticipation;
}): JSX.Element | null {
  const business = useSelectedBusiness();

  if (!business) return null;

  // Check if it's a group expense (no participations or business is the payee)
  if (!declaration.participations || declaration.participations.length === 0) {
    return (
      <div className="flex items-center gap-2">
        <Avatar className="h-8 w-8" src={business.picture ?? undefined} alias={business.name} />
        <span>{business.name}</span>
      </div>
    );
  }

  // It's a personal expense, show the participants
  return <AvatarList participants={declaration.participations} />;
}

// Helper components for table cells
function HeaderCell({ children, ...props }: PropsOf<'th'>) {
  return (
    <th
      {...props}
      className={twMerge(
        'border-b border-secondary-200 bg-secondary-50 px-4 py-2 text-left text-sm font-medium text-primary-900',
        props.className,
      )}>
      {children}
    </th>
  );
}

function DataCell({ children, ...props }: PropsOf<'td'>) {
  return (
    <td
      {...props}
      className={twMerge(
        'border-secondary-100 border-b px-4 py-3 text-sm text-primary-900',
        props.className,
      )}>
      {children}
    </td>
  );
}
